import { alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CatalogCardVariantKey } from '../../../../modules/catalogs/products/v2/sizing/catalogCardColumns.types';

export const CHECKBOX_REVEAL_CLASS = 'card-select-checkbox';
export const TAG_HIDE_CLASSNAME = 'card-tag-hide';
export const PRODUCT_NAME_CLASSNAME = 'card-product-name';

interface ProductCardV2StylesParams {
  size?: CatalogCardVariantKey;
}

const borderRadiusBySize = {
  [CatalogCardVariantKey.SMALL]: 'borderRadiusLg',
  [CatalogCardVariantKey.MEDIUM]: 'borderRadiusXl',
  [CatalogCardVariantKey.LARGE]: 'borderRadius2xl',
} as const satisfies Record<CatalogCardVariantKey, string>;

export const useProductCardV2Styles = makeStyles<ProductCardV2StylesParams>()(
  (theme, { size = CatalogCardVariantKey.SMALL }) => {
    const cardBorderRadius = theme.shape[borderRadiusBySize[size]];

    return {
      root: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        borderRadius: cardBorderRadius,
        gap: theme.spacing(0.5),
        width: '100%',
        [`&:hover .${CHECKBOX_REVEAL_CLASS}`]: {
          width: '28px',
          marginRight: '4px',
          opacity: 1,
          transition: 'width 0.2s ease, margin-right 0.2s ease, opacity 0.2s ease',
        },
        [`&:hover .${TAG_HIDE_CLASSNAME}`]: {
          display: 'none',
        },
      },
      mediaContainer: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: cardBorderRadius,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.06)',
        border: `0.5px solid ${alpha(theme.palette.common.black, 0.1)}`,
      },
      tagsOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1),
        pointerEvents: 'none',
      },
      checkboxWrapper: {
        display: 'flex',
        alignItems: 'center',
        width: 0,
        marginRight: 0,
        opacity: 0,
        overflow: 'hidden',
        flexShrink: 0,
        pointerEvents: 'auto',
      },
      checkboxWrapperVisible: {
        width: '28px',
        marginRight: '4px',
        opacity: 1,
      },
      checkbox: {
        padding: theme.spacing(0.5),
      },
      tagsGroup: {
        display: 'flex',
        gap: theme.spacing(1),
        flex: 1,
        minWidth: 0,
      },
      infoSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(1.25),
        paddingLeft: theme.spacing(0.75),
        paddingRight: theme.spacing(0.75),
        borderRadius: cardBorderRadius,
        cursor: 'pointer',
      },
      infoHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
      },
      infoMain: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        flex: 1,
        minWidth: 0,
      },
      productName: {
        fontWeight: 500,
        color: theme.palette.text.primary,
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      },
      productCode: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textTransform: 'lowercase',
        minWidth: 0,
      },
      fakeInfoSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(1.25),
        paddingLeft: theme.spacing(0.75),
        paddingRight: theme.spacing(0.75),
        borderRadius: cardBorderRadius,
      },
      fakeColorsRow: {
        display: 'flex',
        gap: theme.spacing(0.5),
      },
      fakePriceRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
    };
  },
);
